import { getMedias } from "@omni/mappers/product/shared/media.mapper";
import { CustomerOrderItem } from "@omni/types/api/cart/bff/shared/customerOrderItem.model";
import { DeliveryType } from "@omni/types/api/product/bff/shared/enums/deliveryType.enum";
import { Divider } from "@omni/ui/components/Divider/divider.component";
import * as CustomerOrderUtils from "@omni/utils/customerOrder.utils";
import { Fragment, useMemo } from "react";

import { OrderImage } from "@/components/Account/OrderList/Order/OrderItem/partials/orderImage.component";
import { Row } from "@/components/General/Grid/row.component";
import OrderItemStatus from "@/components/Order/shared/OrderItemStatus/orderItemStatus.component";
import { AdditionalServiceComponent } from "@/components/Product/AdditionalService/additionalService.component";
import { ProductConfigurationDetailComponent } from "@/components/Product/ProductConfigurationDetail/productConfigurationDetail.component";
import { ConfigurableProductRenditions } from "@/components/Product/ProductImage/mappedRenditions";
import { ProductName } from "@/components/Product/ProductName/productName.component";
import { useGranite } from "@/redux/modules/value/translations/translations.provider";
import { Config } from "@/utils/config.utils";
import { formatPrice } from "@/utils/price.utils";
import { getProductConfigurationPreview } from "@/utils/productConfiguration";
import {
    getProductConfiguration,
    productConfigurations
} from "@/utils/productConfiguration/productConfiguration.utils";

type Props = {
    item: CustomerOrderItem;
    index: number;
    orderId: string;
};

const OrderItem = ({ item, index, orderId }: Props) => {
    const { t } = useGranite();

    const productConfiguration = useMemo(
        () => getProductConfiguration({ sku: item.sku }),
        [item.sku]
    );

    return (
        <>
            {index > 0 && <Divider marginTop="2" />}
            <Row className="d-md-none pt-3">
                <div className="col">
                    <ProductName
                        name={CustomerOrderUtils.getTitle(item, t)}
                        url={CustomerOrderUtils.getUrl(
                            item,
                            Config.pages.giftcard
                        )}
                    />
                    {!!item.articleConfiguration && (
                        <div>
                            <ProductConfigurationDetailComponent
                                configuration={item.articleConfiguration}
                            />
                        </div>
                    )}
                </div>
            </Row>
            <Row className="py-3">
                <div className="col-5 col-sm-4 col-md-2 px-md-4">
                    <OrderImage
                        medias={getMedias(
                            item.warrantyDeposit,
                            CustomerOrderUtils.getTitle(item, t),
                            item.mainImages,
                            item.articleConfiguration
                        )}
                        title={CustomerOrderUtils.getTitle(item, t)}
                        url={CustomerOrderUtils.getUrl(
                            item,
                            Config.pages.giftcard
                        )}
                        imageRenditions={
                            item.articleConfiguration
                                ? ConfigurableProductRenditions
                                : undefined
                        }
                    />
                </div>
                <div className="col-4 col-md-7 d-none d-md-flex flex-column ">
                    <Row className="d-none d-md-block">
                        <div className="col">
                            <ProductName
                                name={`${CustomerOrderUtils.getTitle(
                                    item,
                                    t
                                )}  ${getProductConfigurationPreview(
                                    CustomerOrderUtils.getConfiguration(
                                        item,
                                        productConfigurations
                                    ),
                                    productConfiguration
                                )}`}
                                url={CustomerOrderUtils.getUrl(
                                    item,
                                    Config.pages.giftcard
                                )}
                            />
                            {!!item.articleConfiguration && (
                                <div>
                                    <ProductConfigurationDetailComponent
                                        configuration={
                                            item.articleConfiguration
                                        }
                                    />
                                </div>
                            )}
                        </div>
                    </Row>
                    {item.deliveryType === DeliveryType.ClickAndCollect && (
                        <Row>
                            <div className="col-auto text-nowrap">
                                <OrderItemStatus
                                    statuses={item.orderLineStatuses}
                                    deliveryType={item.deliveryType}
                                />
                            </div>
                        </Row>
                    )}
                    {item.deliveryType === DeliveryType.HomeDelivery &&
                        item.transportPackageStatuses.map((status) => (
                            <Fragment
                                key={`f-${orderId}-${status.productSku}-${status.transportStatusMessage}`}
                            >
                                <Row>
                                    <div className="col-auto text-nowrap">
                                        <OrderItemStatus
                                            statuses={item.orderLineStatuses}
                                            deliveryType={item.deliveryType}
                                            transportStatusMessage={
                                                status.transportStatusMessage
                                            }
                                        />
                                    </div>
                                </Row>
                                <Row>
                                    <div className="col justify-content-end">
                                        {status.trackAndTraceUrl && (
                                            <a href={status.trackAndTraceUrl!}>
                                                {t("general.trackAndTrace")}
                                            </a>
                                        )}
                                    </div>
                                </Row>
                            </Fragment>
                        ))}
                </div>
                <div className="col">
                    <Row>
                        <div className="col-auto text-md-left">
                            {item.quantity}
                        </div>
                        <div className="col text-right font-weight-bold pr-md-3">
                            <span>{formatPrice(item.totalPrice)}</span>
                        </div>
                    </Row>
                    <Row className="d-md-none text-nowrap">
                        {item.deliveryType === DeliveryType.ClickAndCollect && (
                            <OrderItemStatus
                                statuses={item.orderLineStatuses}
                                deliveryType={item.deliveryType}
                            />
                        )}
                        {item.deliveryType === DeliveryType.HomeDelivery &&
                            item.transportPackageStatuses.map((status) => (
                                <Fragment
                                    key={`f-${status.productSku}-${status.transportStatusMessage}`}
                                >
                                    <OrderItemStatus
                                        key={`status-${status.productSku}-${status.transportStatusMessage}`}
                                        statuses={item.orderLineStatuses}
                                        deliveryType={item.deliveryType}
                                        transportStatusMessage={
                                            status.transportStatusMessage
                                        }
                                    />
                                    {status.trackAndTraceUrl && (
                                        <div
                                            key={`tnt-${status.productSku}-${status.transportStatusMessage}`}
                                        >
                                            <a href={status.trackAndTraceUrl!}>
                                                {t("general.trackAndTrace")}
                                            </a>
                                        </div>
                                    )}
                                </Fragment>
                            ))}
                    </Row>
                </div>
            </Row>
            {item.additionalServices?.map((service) => (
                <Row
                    key={service.id}
                    className={"pb-3 pl-md-4 pr-md-3"}
                >
                    <div className={"col-9 pl-md-0"}>
                        <AdditionalServiceComponent
                            additionalService={service}
                        />
                    </div>
                    <div
                        className={
                            "col-3 mt-2 text-right font-weight-bold my-auto"
                        }
                    >
                        {formatPrice(service.price)}
                    </div>
                </Row>
            ))}
        </>
    );
};

export default OrderItem;
